import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSiteData } from "@/context/SiteDataContext";
import { fmtDate } from "@/lib/api";
import { sourceLabel } from "@/lib/leads";
import { Panel } from "@/components/admin/common";
import { LeadPipelineFields } from "./LeadFields";

const KEYS = ["name", "phone", "whatsapp", "email", "travel_date", "adults", "children", "destination", "budget", "trip_type", "source",
  "follow_up_date", "follow_up_time", "notes", "status", "quotation_amount"];
const pick = (l) => ({ ...Object.fromEntries(KEYS.map((k) => [k, l[k] ?? ""])), adults: l.adults || l.guests, package_id: l.package_id || "none" });

const Text = ({ id, label, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} className="h-10" data-testid={`${id}-input`} {...p} /></div>
);

export const LeadInfoForm = ({ lead, onSave }) => {
  const { packages } = useSiteData();
  const [f, setF] = useState(pick(lead));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(pick(lead)); }, [lead]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const inp = (k) => (e) => set(k)(e.target.value);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...f, adults: Number(f.adults), children: Number(f.children) || 0, quotation_amount: Number(f.quotation_amount) || 0, package_id: f.package_id === "none" ? "" : f.package_id };
    if (!body.trip_type) delete body.trip_type;
    await onSave(body, "Customer details saved");
    setSaving(false);
  };
  return (
    <Panel className="p-5" data-testid="lead-info-panel">
      <p className="font-semibold text-navy">Customer & trip details</p>
      <p className="text-xs text-slate-500 mt-0.5">Received {fmtDate(lead.created_at)} · {sourceLabel(lead.source)}{lead.source_page ? ` · ${lead.source_page}` : ""}{lead.utm_campaign ? ` · campaign ${lead.utm_campaign}` : ""}</p>
      {lead.message && <p className="mt-3 rounded-xl bg-mist p-3 text-sm text-slate-700 whitespace-pre-line" data-testid="lead-message">“{lead.message}”</p>}
      <form onSubmit={save} className="mt-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Text id="lead-info-name" label="Name" required minLength={2} value={f.name} onChange={inp("name")} />
          <Text id="lead-info-phone" label="Mobile / WhatsApp" required type="tel" pattern="^\+?[0-9][0-9\s\-]{6,19}$" value={f.phone} onChange={inp("phone")} />
          <Text id="lead-info-whatsapp" label="WhatsApp (if different)" type="tel" pattern="^\+?[0-9][0-9\s\-]{6,19}$" value={f.whatsapp} onChange={inp("whatsapp")} />
          <Text id="lead-info-email" label="Email" type="email" value={f.email} onChange={inp("email")} />
          <Text id="lead-info-travel" label="Travel date" type="date" value={f.travel_date} onChange={inp("travel_date")} />
          <Text id="lead-info-adults" label="Adults" type="number" min={1} max={60} required value={f.adults} onChange={inp("adults")} />
          <Text id="lead-info-children" label="Children" type="number" min={0} max={40} value={f.children} onChange={inp("children")} />
          <Text id="lead-info-quote-amount" label="Quotation amount (₹)" type="number" min={0} step="1" value={f.quotation_amount} onChange={inp("quotation_amount")} />
          <div className="space-y-1.5"><Label>Booking status</Label>
            <p className="h-10 px-3 flex items-center rounded-md border border-slate-200 bg-slate-50 text-sm capitalize text-slate-700" data-testid="lead-info-booking-status">{lead.booking_status || "No booking yet"}</p></div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Package</Label>
            <Select value={f.package_id} onValueChange={set("package_id")}>
              <SelectTrigger className="h-10" data-testid="lead-info-package"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72"><SelectItem value="none">No package</SelectItem>{packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <LeadPipelineFields f={f} set={set} prefix="lead-info" />
        <Button type="submit" disabled={saving} className="w-full bg-ocean hover:bg-navy" data-testid="lead-info-save-button">Save details</Button>
      </form>
    </Panel>
  );
};
